import style from "./style.module.css";
import Guide from "@/components/common/guide/guide";
import ContentCard from "@/components/UI/content-card/content_card";
import { Fragment, useState, useEffect, useContext, useRef } from "react";
import Button from "@/components/UI/button/button";
import { useRouter } from "next/router";
import CustomerContext from "@/stores/customer-context";
import Head from "next/head";
import HomeType from "@/components/calculate/home_type";
import HomeStructure from "@/components/calculate/home_structure";
import HomeArea from "@/components/calculate/home_area";
import HomeDropLeadInfo from "@/components/calculate/home_droplead_info";
import { homeInfoExtendedSchema } from "@/helpers/schema_validation";
import { Formik, Form } from "formik";
import useGtm, { GTM_EVENT } from "@/hooks/useGtm";
import _, { isEmpty } from "underscore";
import { hash } from "@/helpers/utility/crypto";
import { HomeProperty } from "@/components/calculate/home_property";
import { ConsentPrivacyPolicy } from "@/components/common/consent-privacy-policy/consent_privacy_policy";

export default function HomeInfo() {
  const customerCtx = useContext(CustomerContext);
  const { onSendGtmEvent } = useGtm("infoForm");
  const router = useRouter();
  const [isHomeArea, setIsHomeArea] = useState(false);
  const [isHomeProperty, setIsHomeProperty] = useState(false);
  const [isDropLead, setIsDropLead] = useState(false);
  const [isValidDropLead, setIsValidDropLead] = useState(false);
  const [homeStructurePrevObj, setHomeStructurePrevObj] = useState();
  const [isCommercialBuilding, setIsCommercialBuilding] = useState(false);
  const [isAbleToCommercialFlow, setIsAbleToCommercialFlow] = useState(false);
  const [homeAreaPrevObj, setHomeAreaPrevObj] = useState();
  const [clickSubmit, setClickSubmit] = useState(false);
  const [initialValues, setInitialValues] = useState({
    homeStructure: "",
    homeType: "",
    homeFloor: "",
    homeUsage: "",
    homeSqm: "",
    homeExtraSqm: "",
    homeFurniture: 0,
    homeApplicant: 0,
    firstName: "",
    lastName: "",
    phone: "",
  });
  const formik = useRef(initialValues);
  const [reinitialise, setReinitialise] = useState(true);
  const _compareKeys = (baseObject = {}, compareKeys = []) => {
    let isAllMatched = true;
    compareKeys.forEach((compare) => {
      if (baseObject[compare] != undefined) {
        isAllMatched = false;
      }
    });
    return isAllMatched;
  };
  const _showComponent = () => {
    let showHomeArea = false;
    let showDroplead = false;
    let showHomeProperty = false;
    const form = formik.current;
    const _errors = form.errors;
    const { homeUsage } = form.values;
    const _isCommerCialBuilding = homeUsage.includes("commercial");
    if (_isCommerCialBuilding) {
      setIsCommercialBuilding(true);
    } else {
      setIsCommercialBuilding(false);
    }
    showHomeArea = _compareKeys(_errors, [
      "homeType",
      "homeFloor",
      "homeStructure",
      "homeUsage",
    ]);
    showHomeProperty = showHomeArea && _compareKeys(_errors, ["homeSqm"]);
    showDroplead =
      showHomeArea &&
      showHomeProperty &&
      _compareKeys(_errors, ["homeFurniture", "homeApplicant"]);
    if (showHomeArea && !_isCommerCialBuilding) {
      setIsHomeArea(true);
      //Prevent multiple event sending
      if (!isHomeArea) {
        onSendGtmEvent(GTM_EVENT.CALCULATE, {}, "show_home_area");
      }
    } else if (showHomeArea && _isCommerCialBuilding) {
      setIsAbleToCommercialFlow(true);
      form.setFieldValue("homeSqm", 0);
      form.setFieldValue("homeExtraSqm", 0);
      showDroplead = false;
      setIsHomeArea(false);
      setIsDropLead(false);
      setIsHomeProperty(false);
    } else {
      setIsHomeArea(false);
    }
    if (showHomeProperty) {
      setIsHomeProperty(true);
      //Prevent multiple event sending
      if (!isHomeArea) {
        onSendGtmEvent(GTM_EVENT.CALCULATE, {}, "show_home_prop");
      }
    }
    if (showDroplead) {
      setIsDropLead(true);
      //Prevent multiple event sending
      if (!isDropLead) {
        onSendGtmEvent(GTM_EVENT.CALCULATE, {}, "show_droplead");
      }
    }
    return [showHomeArea, showHomeProperty, showDroplead];
  };
  const onSubmitHandler = async (customerHomeInfo) => {
    if (!customerHomeInfo) return;

    const _customerInfo = {
      firstName: customerHomeInfo.firstName,
      lastName: customerHomeInfo.lastName,
      phone: customerHomeInfo.phone,
    };
    const _homeInfo = {
      homeStructure: customerHomeInfo.homeStructure,
      homeType: customerHomeInfo.homeType,
      homeFloor: customerHomeInfo.homeFloor,
      homeUsage: customerHomeInfo.homeUsage,
      homeSqm: customerHomeInfo.homeSqm,
      homeExtraSqm: customerHomeInfo.homeExtraSqm,
      homeFurniture: customerHomeInfo.homeFurniture,
      homeApplicant: customerHomeInfo.homeApplicant,
      homeAsset: customerHomeInfo.homeAsset,
    };
    await customerCtx.setCustomerInfo(_customerInfo, "droplead");
    await customerCtx.setHomeInfo(_homeInfo);

    const hashedPhone = await hash(customerHomeInfo.phone);
    const _userId = await customerCtx.getUserId();
    const gtmEvent = {
      transaction_id: _userId,
      phone: hashedPhone,
    };
    onSendGtmEvent(GTM_EVENT.DROP_LEAD, gtmEvent, "calculate_form");
    router.push("/calculate-plan/home-location");
  };

  const onFormChangeHandler = async (event) => {
    //Set timer
    //Todo debounce
    setTimeout(async () => {
      const [showHomeArea, showHomeProperty, showDroplead] = _showComponent();
      if (showHomeArea) {
        const { homeType, homeFloor, homeStructure, homeUsage } =
          formik.current.values;
        const _homeInfo = {
          homeType,
          homeFloor,
          homeStructure,
          homeUsage,
        };
        if (!_.isEqual(_homeInfo, homeStructurePrevObj)) {
          await customerCtx.setHomeInfo(_homeInfo);
          setHomeStructurePrevObj(_homeInfo);
        }
      }
      if (showHomeProperty) {
        const { homeSqm, homeExtraSqm } = formik.current.values;
        const _homeInfo = { homeSqm, homeExtraSqm };
        if (!_.isEqual(_homeInfo, homeAreaPrevObj)) {
          await customerCtx.setHomeInfo(_homeInfo);
          setHomeAreaPrevObj(_homeInfo);
        }
      }
      if (showDroplead) {
        const { homeFurniture, homeApplicant } = formik.current.values;
        const _homeInfo = { homeFurniture, homeApplicant };
        if (!_.isEqual(_homeInfo, homeAreaPrevObj)) {
          await customerCtx.setHomeInfo(_homeInfo);
          setHomeAreaPrevObj(_homeInfo);
        }
      }
      const _errors = formik.current.errors;
      const validDropLead = _compareKeys(_errors, [
        "firstName",
        "lastName",
        "phone",
      ]);
      if (validDropLead) {
        setIsValidDropLead(true);
      }
    }, 1);
  };

  useEffect(() => {
    (async () => {
      const _homeInfo = await customerCtx.getHomeInfo();
      setInitialValues({ ...initialValues, ..._homeInfo });
      setTimeout(() => {
        _showComponent();
      }, 1);
    })();
  }, []);

  const onChangeFlowClickHandler = () => {
    router.push("/smes/form");
  };

  return (
    <Fragment>
      <Head>
        <title>กรอกมูลค่าสินทรัพย์ - บริษัท TQM</title>
      </Head>
      <section className={style.home_info_section}>
        <div className={style.home_info_container}>
          <div className={style.title}>
            <div className={style.guide}>
              <Guide state={0} />
            </div>
          </div>
          <ContentCard newStyleContentCard={true} cardContainer={true}>
            <Formik
              innerRef={formik}
              initialValues={initialValues}
              onSubmit={onSubmitHandler}
              validationSchema={homeInfoExtendedSchema()}
              enableReinitialize={reinitialise}
              validateOnChange={true}
              validateOnMount={true}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldTouched,
                setFieldError,
                setErrors,
              }) => (
                <Form
                  className={style.customer_form}
                  onChange={onFormChangeHandler}
                >
                  <div className={`${style.form_section}`}>
                    <ContentCard
                      header="รายละเอียดที่อยู่อาศัย"
                      newStyleContentCard={true}
                    >
                      <HomeType
                        errors={errors}
                        touched={touched}
                        onChange={handleChange}
                      />
                      <HomeStructure
                        values={values}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                      />
                    </ContentCard>
                  </div>
                  <div
                    className={`${style.form_section} ${
                      isHomeArea || "hidden"
                    }`}
                  >
                    <ContentCard
                      header="พื้นที่ใช้สอย (ตร.ม.)"
                      newStyleContentCard={true}
                    >
                      <HomeArea
                        values={values}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                      />
                    </ContentCard>
                  </div>
                  <div
                    className={`${style.form_section} ${
                      isHomeProperty || "hidden"
                    }`}
                  >
                    <ContentCard
                      header="มูลค่าทรัพย์สินภายในตัวอาคาร"
                      newStyleContentCard={true}
                    >
                      <HomeProperty
                        values={values}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                      />
                    </ContentCard>
                  </div>
                  <div
                    className={`${style.form_section} ${
                      isDropLead ? "" : "hidden"
                    }`}
                  >
                    <ContentCard
                      header="ข้อมูลผู้เอาประกัน"
                      newStyleContentCard={true}
                    >
                      <HomeDropLeadInfo
                        values={values}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errors={errors}
                        touched={touched}
                        setFieldValue={setFieldValue}
                      />
                    </ContentCard>
                  </div>
                  <ConsentPrivacyPolicy
                    clickSubmit={clickSubmit}
                    setClickSubmit={setClickSubmit}
                    onSubmit={() => {
                      if (isCommercialBuilding) {
                        onChangeFlowClickHandler();
                        return;
                      } else {
                        if (!isCommercialBuilding && isEmpty(touched)) return;
                        onSubmitHandler(formik?.current?.values);
                      }
                    }}
                  />
                  <div
                    className={`${style.next_btn} ${
                      isCommercialBuilding ? "shown" : ""
                    }`}
                  >
                    <Button
                      color="blue"
                      text="ถัดไป"
                      enable={isAbleToCommercialFlow ? true : false}
                      onClick={() => setClickSubmit(true)}
                    />
                  </div>
                  <div className={style.submit_btn}>
                    <Button
                      color="blue"
                      text="ถัดไป"
                      enable={isValidDropLead ? true : false}
                      onClick={(e) => {
                        e.preventDefault();
                        setClickSubmit(true);
                      }}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </ContentCard>
        </div>
      </section>
    </Fragment>
  );
}
